import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ISPM_BASE_URL } from '../../utils/constants';
import { setToken, removeToken, getToken, isAuthenticated } from '../../services/api';
import {cookieService} from "../../services/cookieService"

// Login async thunk
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${API_ISPM_BASE_URL}/auth/login`, {
        username: username.trim(),
        password: password.trim(),
      });

      if (response?.data?.success) {
        const { data } = response.data;
        const token = data?.token;
        const role= data?.role;
        const userId =data?.userId;

        if (!token) {
          return rejectWithValue('No token received from server.');
        }

        // Store token using existing service
        setToken(token, username, role,userId);

        return {
          token,
          username,
          user: data?.user || null,
          role:data?.role,
          userId:data?.userId,
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Invalid credentials.');
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Connection failed. Please try again.'
      );
    }
  }
);

// Initialize from storage
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      if (isAuthenticated()) {
        const token = getToken();
        const userObj = JSON.parse(localStorage.getItem("currentUser") || "{}");
        const username = userObj?.userName;
        const role = userObj?.role;
        return { token, username, role };
      }
      return null;
    } catch (error) {
      return rejectWithValue('Failed to initialize auth');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    // User data
    user: null,
    token: null,
    username: null,
    role:null,
    isAuthenticated: false,

    // Loading states
    loading: false,
    isInitializing: true,

    // Error handling
    error: null,

    // UI states
    showPassword: false,
    showForgotModal: false,
  },
  reducers: {
    // Toggle password visibility
    togglePasswordVisibility: (state) => {
      state.showPassword = !state.showPassword;
    },

    // Toggle forgot password modal
    toggleForgotModal: (state, action) => {
      state.showForgotModal = action.payload ?? !state.showForgotModal;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Logout and reset - this action will trigger root reducer reset
    logout: (state) => {
      removeToken();
      // if (cookieService.clearSyncCookies) {
      //   cookieService.clearSyncCookies();
      // }
      localStorage.clear()
      state.user = null;
      state.token = null;
      state.username = null;
      state.role =null;
      state.isAuthenticated = false;
      state.error = null;
      state.showPassword = false;
      state.showForgotModal = false;
    },

    // Set auth from context
    setAuthFromContext: (state, action) => {
      if (action.payload) {
        state.token = action.payload.token;
        state.isAuthenticated = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.isInitializing = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isInitializing = false;
        if (action.payload) {
          state.token = action.payload.token;
          state.username = action.payload.username;
          state.isAuthenticated = true;
          state.role = action.payload.role;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isInitializing = false;
        state.isAuthenticated = false;
      })

      // Login User
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.username = action.payload.username;
        state.user = action.payload.user;
        state.role =action.payload.role;
        state.isAuthenticated = true;
        state.error = null;
        state.showPassword = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });
  },
});

export const {
  togglePasswordVisibility,
  toggleForgotModal,
  clearError,
  logout,
  setAuthFromContext,
} = authSlice.actions;

export default authSlice.reducer;